import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { motion } from 'framer-motion';
import { conditionalScaleAnimation } from 'libs/styles/animations';
import { EBorders, EFonts, EHovers } from 'libs/styles/styling';
import { useContext } from 'react';
import { CtxEdit } from 'session/ctxEdit';
import { Localization } from 'static/localization/localization';
import styled, { css } from 'styled-components/macro';

interface Props {
    className?: string;
}

export const HistoryNavigationControl = (props: Props): JSX.Element => {
    const ctxHistory = useContext(CtxEdit);

    function goBack() {
        ctxHistory.editHistory.actions.goBackInHistory();
    }

    function goNext() {
        ctxHistory.editHistory.actions.goForwardInHistory();
    }

    function isBackAllowed() {
        return ctxHistory.editHistory.state.markerHistoryPointer === null;
    }

    function isForwardAllowed() {
        return (
            !ctxHistory.editHistory.state.markerHistory.length ||
            ctxHistory.editHistory.state.markerHistoryPointer ===
                ctxHistory.editHistory.state.markerHistory.length - 1
        );
    }

    return (
        <S_HistoryArrows className={props.className}>
            <S_Button
                $isActive={!isBackAllowed()}
                $alignLeft={false}
                onClick={() => {
                    if (!isBackAllowed()) goBack();
                }}
            >
                <motion.div {...conditionalScaleAnimation(!isBackAllowed(), 1.2, 0.9)}>
                    <S_Icon $isActive={!isBackAllowed()} icon={['fal', 'arrow-left']} />
                </motion.div>
            </S_Button>

            <S_Text>{Localization.HISTORY}</S_Text>

            <S_Button
                $isActive={!isForwardAllowed()}
                $alignLeft={true}
                onClick={() => {
                    if (!isForwardAllowed()) goNext();
                }}
            >
                <motion.div {...conditionalScaleAnimation(!isForwardAllowed(), 1.2, 0.9)}>
                    <S_Icon $isActive={!isForwardAllowed()} icon={['fal', 'arrow-right']} />
                </motion.div>
            </S_Button>
        </S_HistoryArrows>
    );
};

const S_HistoryArrows = styled.div`
    display: flex;
    position: absolute;
    top: 15px;
    left: 15px;
    width: 157px;
    border-radius: 7px;
    background: #efefef;
    z-index: 1;
    align-items: center;
    box-shadow: 5px 5px 10px 0px rgba(0, 0, 0, 0.1);
    padding: 5px 11px;
    user-select: none;
    border: 1px solid ${EBorders.DARK};
`;

const S_Icon = styled(FontAwesomeIcon)<{ $isActive: boolean }>`
    font-size: 20px;
    color: #a9a9a9;
    width: 20px;
    ${(props) => {
        if (props.$isActive) {
            return css`
                color: #141c54;
            `;
        }
    }}
`;

const S_Button = styled.div<{ $isActive: boolean; $alignLeft: boolean }>`
    width: 30px;
    height: 30px;
    display: flex;
    color: #e6e6e6;
    justify-content: center;
    align-items: center;
    cursor: not-allowed;
    ${(props) => {
        if (props.$alignLeft) {
            return css`
                margin-left: auto;
            `;
        } else {
            return css`
                margin-right: 13px;
            `;
        }
    }}
    ${(props) => {
        if (props.$isActive) {
            return css`
                cursor: pointer;
                &:hover {
                    box-shadow: ${EHovers.LIGHT1};
                }
            `;
        }
    }}
`;

const S_Text = styled.div`
    font-size: 13px;
    font-family: ${EFonts.Regular};
    color: #141c54;
    width: 60px;
    text-align: left;
`;
